import { Alert, AlertIcon, Button, FormControl, FormErrorMessage, FormHelperText, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type SourcingEventDto,
  useGetSourcingEventQuery,
  useStartSeClarificationMutation,
} from "../../../autogen/bff-api";
import { AttachmentPicker } from "../../../common/components/communication/AttachmentPicker";
import { richTextHasContent } from "../../../common/components/communication/communicationUtils";
import { useMessageAttachments } from "../../../common/components/communication/useMessageAttachments";
import { useApiError } from "../../../common/errors/useApiError";
import { TipTap } from "../../../common/input/TipTap/TipTap";
import moment from "moment";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { ThemedDateTimePicker } from "../../../common/input/DateTimeSelectors/ThemedDateTimePicker";
import {
  clarificationNeedsReplyDeadline,
  clarificationRuleOf,
  replyDeadlineAfterTenderValidity,
  tenderValidityHasPassed,
  replyDeadlineLongerThanShort,
  runningTenderValidityEnd,
  suggestedReplyDeadline,
} from "./clarificationRule";

export interface ClarificationCounterparty {
  organizationId: string;
  organizationName: string;
}

export const StartClarificationModal = ({
  eventId,
  counterparties,
  fixedCounterpartyId,
  isOpen,
  onClose,
  event: knownEvent,
  initialContent = "",
}: {
  event?: SourcingEventDto;
  initialContent?: string;
  eventId: string;
  counterparties: ClarificationCounterparty[];
  fixedCounterpartyId?: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const [start, { isLoading }] = useStartSeClarificationMutation();
  const { data: fetchedEvent } = useGetSourcingEventQuery({ eventId }, { skip: knownEvent != null });
  const event = knownEvent ?? fetchedEvent;
  const clarificationRule = clarificationRuleOf(event);
  const requestDocuments = (event?.eventData.documents ?? []).map((d) => ({ id: d.id, name: d.name }));

  const [supplierOrganizationId, setSupplierOrganizationId] = useState(
    fixedCounterpartyId ?? counterparties[0]?.organizationId ?? "",
  );
  const deadlineRequired = clarificationNeedsReplyDeadline(event);
  const [chosenDeadline, setReplyDeadline] = useState<string | null | undefined>(undefined);
  const [suggestion] = useState(() => suggestedReplyDeadline().toISOString());
  const replyDeadline = chosenDeadline === undefined ? (deadlineRequired ? suggestion : null) : chosenDeadline;
  const deadlineInPast = replyDeadline != null && !moment(replyDeadline).isAfter(moment());
  const deadlineAfterTenderValidity = replyDeadline != null && replyDeadlineAfterTenderValidity(event, replyDeadline);
  const deadlineLongerThanShort =
    deadlineRequired &&
    replyDeadline != null &&
    !deadlineInPast &&
    !deadlineAfterTenderValidity &&
    replyDeadlineLongerThanShort(replyDeadline);
  const tenderValidityEnd = runningTenderValidityEnd(event);
  const bindingPeriodHasRunOut = tenderValidityHasPassed(event);
  const displayDate = useDisplayDate();
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState(initialContent);
  const [resetKey, setResetKey] = useState(0);
  // The answer upload is question-scoped and there is no thread yet; the announcement upload is the buyer's
  // event-scoped one, and it authorizes exactly what is needed here (the buyer, on this event). The file only
  // becomes a clarification attachment when the dialog is created with it.
  const attachments = useMessageAttachments({
    eventId,
    uploadEntityType: "SeV2AnnouncementAttachment",
    pollRole: "buyer",
  });

  const canSend =
    supplierOrganizationId.length > 0 &&
    subject.trim().length > 0 &&
    richTextHasContent(content) &&
    !attachments.isUploading &&
    !attachments.hasFailedUploads &&
    (!deadlineRequired || replyDeadline != null) &&
    !deadlineInPast &&
    !deadlineAfterTenderValidity;

  const handleSend = async () => {
    if (!canSend) return;
    try {
      await start({
        eventId,
        createSeClarificationRequest: {
          supplierOrganizationId,
          subject: subject.trim(),
          content,
          attachments: attachments.toInputs(),
          replyDeadline: replyDeadline ?? undefined,
        },
      }).unwrap();
      setSubject("");
      setContent("");
      attachments.reset();
      setResetKey((key) => key + 1);
      onClose();
    } catch (error) {
      displayer.trigger(error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Start a clarification dialog")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Alert status="info" rounded="md" fontSize="sm">
              <AlertIcon />
              {clarificationRule === "FOA_PART_II"
                ? t("In Part II the dialogue may cover every side of the tender, negotiations included (FOA § 9-3).")
                : clarificationRule === "FOA_PART_III"
                  ? t(
                      "A clarification cannot change the tender — it may only clarify what has already been submitted (FOA § 23-5).",
                    )
                  : t("A clarification cannot change the tender — it may only clarify what has already been submitted.")}
            </Alert>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Tenderer")}</FormLabel>
              <Select
                bg="smBackground"
                value={supplierOrganizationId}
                isDisabled={!!fixedCounterpartyId}
                onChange={(e) => setSupplierOrganizationId(e.target.value)}
              >
                {counterparties.map((counterparty) => (
                  <option key={counterparty.organizationId} value={counterparty.organizationId}>
                    {counterparty.organizationName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Subject")}</FormLabel>
              <Input bg="smBackground" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Message")}</FormLabel>
              <TipTap
                key={resetKey}
                content={resetKey === 0 ? initialContent : ""}
                editable
                debounceWait={0}
                onUpdate={(value) => setContent(value)}
              />
            </FormControl>
            <AttachmentPicker controls={attachments} requestDocuments={requestDocuments} />
            <FormControl isRequired={deadlineRequired} isInvalid={deadlineInPast || deadlineAfterTenderValidity} maxW="sm">
              <FormLabel fontSize="sm" htmlFor="clarificationReplyDeadline">
                {t("Reply deadline")}
              </FormLabel>
              <ThemedDateTimePicker
                id="clarificationReplyDeadline"
                ariaLabel={t("Reply deadline") ?? ""}
                date={replyDeadline}
                minDate={new Date()}
                maxDate={tenderValidityEnd ? new Date(tenderValidityEnd) : undefined}
                isClearable={!deadlineRequired}
                onChange={(iso) => setReplyDeadline(iso ?? null)}
              />
              {deadlineInPast ? (
                <FormErrorMessage>{t("zz_FieldErrorCode_ReplyDeadlineInPast")}</FormErrorMessage>
              ) : deadlineAfterTenderValidity ? (
                <FormErrorMessage>
                  {t("The reply deadline must lie within the tenders' binding period, which ends {{date}}.", {
                    date: tenderValidityEnd ? displayDate(tenderValidityEnd) : "",
                  })}
                </FormErrorMessage>
              ) : deadlineLongerThanShort ? (
                <FormHelperText color="smWarning">
                  {t("This is longer than a short additional deadline of 10 working days. The request can still be sent.")}
                </FormHelperText>
              ) : (
                <FormHelperText>
                  {deadlineRequired
                    ? t("A short additional deadline for the tenderer's reply. The tenderer sees it in the dialog and in the notification.")
                    : t("Optional. The tenderer sees it in the dialog and in the notification.")}
                </FormHelperText>
              )}
              {bindingPeriodHasRunOut && (
                <Text fontSize="sm" color="smWarning" mt="2">
                  {t("The tenders' binding period has run out. Has the tenderer extended it?")}
                </Text>
              )}
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter justifyContent="flex-start" gap="3">
          <Button colorScheme="teal" onClick={handleSend} isLoading={isLoading} isDisabled={!canSend}>
            {t("Send message")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
